import React, { useEffect, useState } from 'react';
import { Document, Page, Text, View, StyleSheet, Image, PDFViewer, Font } from '@react-pdf/renderer';

// Register a font with bold weight
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: `${process.env.PUBLIC_URL}/assets/fonts/Roboto-Regular.ttf`,
      fontWeight: 'normal',
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/fonts/Roboto-SemiBold.ttf`,
      fontWeight: 'bold',
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    padding: 10,
  },
  section: {
    marginBottom: 10,
  },
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    borderBottomWidth: 0
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableColHeader: {
    width: '33.33%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    borderTopWidth: 0,
    borderRightWidth: 0,
    backgroundColor: '#d3d3d3', // Light gray background color for header
  },
  tableCol: {
    width: '33.33%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    borderTopWidth: 0,
    borderRightWidth: 0,
  },
  tableCell: {
    fontSize: 10,
    fontFamily: 'Roboto'
  },
  tableCellBold: {
    fontSize: 10,
    fontFamily: 'Roboto',
    fontWeight: 'bold'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  logo: {
    width: 200,
    height: 80,
  },
  date: {
    fontSize: 12,
    fontFamily: 'Roboto'
  },
  sideHeader: {
    fontSize: 24,
    fontFamily: 'Roboto'
  },
  sideContent:{
    flexDirection: 'column',
  }
});

type Props = {
    data: any,
    selected: any,
    pdfViewerProps: boolean
}

// Create Document Component
const ShippingReceipt = ({ data, selected, pdfViewerProps }: Props) => {
    const [currentDate, setCurrentDate] = useState('');

    useEffect(() => {
        const date = new Date();
        const formattedDate = date.toLocaleDateString(); // Formats the date as 'MM/DD/YYYY'
        setCurrentDate(formattedDate);
    }, [])
    const formatTime = (date:any)=>{
        const newDate = new Date(date);

        // Extract the time in HH:MM:SS format
        const hours = newDate.getHours().toString().padStart(2, '0');
        const minutes = newDate.getMinutes().toString().padStart(2, '0');
        const seconds = newDate.getSeconds().toString().padStart(2, '0');

        // Combine into the desired format
        return `${hours}:${minutes}:${seconds}`;
    }

    
    const elements = (
        <Document title={`${data.asset_ID}`}>
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Image
                        style={styles.logo}
                        src="/assets/HFC.jpg" // Replace with your logo path
                    />
                    <View style={styles.sideContent}>
                        <Text style={styles.sideHeader}>Shipping Receipt</Text>
                        <Text style={styles.date}>Order No: SHPORD{selected.order_ID}</Text>
                        <Text style={styles.date}>Date: {currentDate}</Text>
                    </View>
                </View> 
                <View style={[styles.table, {marginTop: 10}]}>
                    {/* Table Header */}
                    <View style={styles.tableRow}>
                        <View style={[styles.tableColHeader, { borderLeftWidth: 0, padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Shipping Facility</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Contact Information</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Delivery To</Text>
                        </View>
                    </View>
                    {/* Table Content */}
                    <View style={styles.tableRow}>
                        <View style={[styles.tableCol, { borderLeftWidth: 0, padding: '5px' }]}>
                            <Text style={styles.tableCell}>{selected?.contact_Name}</Text>
                            <Text style={styles.tableCell}>{selected?.address}</Text>
                        </View>
                        <View style={[styles.tableCol, { padding: '5px' }]}>
                            <Text style={styles.tableCell}>Requested By: {data.requested_By} </Text>
                            <Text style={styles.tableCell}>Requestor Email: {data.requester_Email}</Text>
                        </View>
                        <View style={[styles.tableCol, { padding: '5px' }]}>
                            <Text style={styles.tableCell}>Shipping Location: {data.shipping_Location}</Text>
                            <Text style={styles.tableCell}>Address: {data.address}</Text>
                            <Text style={styles.tableCell}>Courier: </Text>
                            <Text style={styles.tableCell}>Account#: {data.account}</Text>
                        </View>
                    </View>
                </View>
                <View style={[styles.table, {marginTop: 10}]}>
                    <View style={styles.tableRow}>
                        <View style={[styles.tableColHeader, { borderLeftWidth: 0, padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Title</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Asset ID</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Container Barcode</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Reel Barcode</Text>
                        </View>
                    </View>
                    {selected?.containerBarCodes &&
                        selected?.containerBarCodes.map((container: any, index: any) => (
                            <React.Fragment key={index}>
                                {container.reel_Barcodes.map((reelBarcode: any, idx: any) => (
                                    <View key={idx} style={styles.tableRow}>
                                        <View style={[styles.tableCol, { borderLeftWidth: 0, padding: '5px' }]}>
                                            <Text style={styles.tableCell}>{data.title}</Text>
                                        </View>
                                        <View style={[styles.tableCol, { padding: '5px' }]}>
                                            <Text style={styles.tableCell}>{selected?.asset_ID}</Text>
                                        </View>
                                        <View style={[styles.tableCol, { padding: '5px' }]}>
                                            <Text style={styles.tableCell}>
                                                {container.container_Barcode}
                                            </Text>
                                        </View>
                                        <View style={[styles.tableCol, { padding: '5px' }]}>
                                            <Text style={styles.tableCell}>{reelBarcode}</Text>
                                        </View>
                                    </View>
                                ))}
                            </React.Fragment>
                        ))}
                </View>
                <View style={[styles.table, {marginTop: 10}]}>
                    <View style={styles.tableRow}>
                        <View style={[styles.tableColHeader, { borderLeftWidth: 0, padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Shipping Date</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Shipping Time</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Printed Name</Text>
                        </View>
                        <View style={[styles.tableColHeader, { padding: '5px' }]}>
                            <Text style={{ fontSize: 10, fontFamily: 'Roboto', fontWeight: 'bold' }}>Signature</Text>
                        </View>
                    </View>
                    <View style={styles.tableRow}>
                        <View style={[styles.tableCol, { borderLeftWidth: 0, padding: '10px' }]}>
                            <Text style={styles.tableCell}>{data.shipping_Date}</Text>
                        </View>
                        <View style={[styles.tableCol, { padding: '10px' }]}>
                            <Text style={styles.tableCell}>{formatTime(selected.date)}</Text>
                        </View>
                        <View style={[styles.tableCol, { padding: '10px' }]}>
                            <Text style={styles.tableCell}>{selected.company_Name}</Text>
                        </View>
                        <View style={[styles.tableCol, { padding: '10px' }]}>
                            <Text style={styles.tableCell}></Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );

    return (
        pdfViewerProps ? (
            <PDFViewer width="100%" height="600">
                {elements}
            </PDFViewer>
        ) : elements
    );
};

export default ShippingReceipt;
